import { NgModule } from '@angular/core'
import { Routes, RouterModule} from '@angular/router'
import { ProposedTopicsPage } from '../../pages/proposed-topics-page'
import { PlanPage } from '../../pages/plan-page'
import { PaymentInfoPage } from '../../pages/payment-info-page'
import { NcRegistrationPage } from '../../pages/new-customer/nc-registration-page'
import { CustomerWriterProfilePage } from '../../pages/customer-writer-profile-page'
import { PublicWriterProfilePage } from '../../pages/public-writer-profile-page'
import { PricingPage } from '../../pages/pricing-page'

/* Customer Sign up */
import { NewCustomerPage } from '../../pages/new-customer/new-customer-page'
import { AudienceAndVoicePage } from '../../pages/new-customer/about/audience-and-voice-page'
import { IndustryPage } from 'src/pages/new-customer/about/industry-page'
import { TonePage } from 'src/pages/new-customer/about/tone-page'
import { OnlinePresencePage } from 'src/pages/new-customer/about/online-presence-page'
import { OtherInfoPage } from 'src/pages/new-customer/about/other-info-page'
import { NcTopicsPage } from 'src/pages/new-customer/nc-topics-page'
import { NcPlanPage } from 'src/pages/new-customer/nc-plan-page'
import { NcMultipassPlanPage } from 'src/pages/new-customer/nc-multipass-plan-page'
import { NcPaymentPage } from 'src/pages/new-customer/nc-payment-page'

/* Agency Sign up */
import { NewAccountPage } from 'src/pages/new-account/new-account-page'
import { AccountSignupHomePage } from 'src/pages/new-account/account-signup-home-page'
import { ClientNamePage } from 'src/pages/new-account/client-name-page'
import { AccountSignupClientsHomePage } from 'src/pages/new-account/account-signup-clients-home-page'
import { ClientConfigurationPage } from 'src/pages/new-account/client-configuration-page'
import { DashboardPage } from 'src/pages/dashboard-page/dashboard-page.component'

import { LoginGuard } from '../../guards/login-guard'
import { LogoutGuard } from '../../guards/logout-guard'
import { FullySetupGuard } from '../../guards/fully-setup-guard'
import { NotSetupGuard } from '../../guards/not-setup-guard'
import { EarliestUnfinishedRouteGuard } from '../../guards/earliest-unfinished-route-guard'
import { ApolloLoginGuard } from 'src/guards/apollo-login-guard'
import { UserWriterGuard } from 'src/guards/user-writer-guard'
import { UserCustomerGuard } from 'src/guards/user-customer-guard'

import { CanDeactivateAudienceAndVoicePage } from 'src/pages/new-customer/about/audience-and-voice-page'
import { CanDeactivateIndustryPage } from 'src/pages/new-customer/about/industry-page'
import { CanDeactivateOnlinePresencePage } from 'src/pages/new-customer/about/online-presence-page'
import { CanDeactivateTonePage } from 'src/pages/new-customer/about/tone-page'
import { CanDeactivateOtherInfoPage } from 'src/pages/new-customer/about/other-info-page'
import { AppPreloadingStrategy } from './app-preloading-strategy'
import { CanDeactivatePostsPage } from '../submissions/queued/queued-submissions-page/queued-submissions-page'

import { MultipassPlanPage } from 'src/pages/multipass-plan-page'
import { NewCustomerPageV1 } from 'src/pages/new-customer-v1/new-customer-page'
import { CanDeactivateIndustryPageV1, IndustryPageV1 } from 'src/pages/new-customer-v1/about/industry-page'
import { CanDeactivateTonePageV1, TonePageV1 } from 'src/pages/new-customer-v1/about/tone-page'
import { CanDeactivateOnlinePresencePageV1, OnlinePresencePageV1 } from 'src/pages/new-customer-v1/about/online-presence-page'
import { AudienceAndVoicePageV1, CanDeactivateAudienceAndVoicePageV1 } from 'src/pages/new-customer-v1/about/audience-and-voice-page'
import { CanDeactivateOtherInfoPageV1, OtherInfoPageV1 } from 'src/pages/new-customer-v1/about/other-info-page'
import { NcTopicsPageV1 } from 'src/pages/new-customer-v1/nc-topics-page'
import { NcPlanPageV1 } from 'src/pages/new-customer-v1/nc-plan-page'
import { NcMultipassPlanPageV1 } from 'src/pages/new-customer-v1/nc-multipass-plan-page'
import { NcPaymentPageV1 } from 'src/pages/new-customer-v1/nc-payment-page'
import { EarliestUnfinishedRouteGuardV1 } from 'src/guards/earliest-unfinished-route-guard-v1'

export const routes: Routes = [
  { path: 'styleguide', loadChildren: () => import('../style-guide/style-guide.module').then(m => m.StyleGuideModule) },
  { path: 'pricing', component: PricingPage },
  /* Edit */
  { path: 'dashboard',
    children: [
      // Public dashboard routes
      { path: 'writer_profile/:writerSlug', component: PublicWriterProfilePage },
      {
        path: '', canActivate: [UserCustomerGuard, FullySetupGuard], runGuardsAndResolvers: 'always',
        // Protected dashboard routes
        children: [
          { path: '', component: DashboardPage,
            children: [
              { path: '', redirectTo: 'submissions', pathMatch: 'full' },
              { path: 'submissions', loadChildren: () => import('../submissions/submissions.module').then(m => m.SubmissionsModule) }
            ]
          },
          { path: 'white_label_profile', loadChildren: () => import('../white-label-profile/white-label.module').then(m => m.WhiteLabelModule) },
          {
            path: 'customers', component: DashboardPage,
            children: [
              { path: 'new', loadChildren: () => import('../new-subscription/new-subscription.module').then(m => m.NewSubscriptionModule) },
            ]
          },
          { path: 'billing_info/edit', component: PaymentInfoPage },
          { path: ':customerId/writer_profile/:writerSlug', component: CustomerWriterProfilePage },
          { path: ':customerId/kickstart', loadChildren: () => import('../kickstart/kickstart.module').then(m => m.KickstartModule) },
          { path: ':customerId/edit_requests/:postId', loadChildren: () => import('../post-request-edits/post-request-edits.module').then(m => m.PostRequestEditsComponentModule) },
          { path: ':customerId', component: DashboardPage,
            children: [
              // Deprecated routes that may be cached
              { path: 'queued', redirectTo: 'submissions?group=queued', pathMatch: 'full' },
              { path: 'purchased', redirectTo: 'submissions?group=purchased', pathMatch: 'full' },
              { path: 'withdrawn', redirectTo: 'submissions?group=withdrawn', pathMatch: 'full' },
              { path: 'declined', redirectTo: 'submissions?group=declined', pathMatch: 'full' },

              { path: '', redirectTo: 'submissions', pathMatch: 'full' },
              { path: 'submissions', loadChildren: () => import('../submissions/submissions.module').then(m => m.SubmissionsModule), data: { preload: 'customer' } },
              { path: 'topics', loadChildren: () => import('../keywords/keywords.module').then(m => m.KeywordsModule), data: { preload: 'customer' } },
              { path: 'proposed_topics', component: ProposedTopicsPage },
              { path: 'business_info', loadChildren: () => import('../business-info/business-info-page.module').then(m => m.BusinessInfoPageModule) },
              { path: 'plan', component: PlanPage },
              { path: 'multipass-plan', component: MultipassPlanPage },
              { path: 'delivery_options', loadChildren: () => import('../delivery-options/delivery-options.module').then(m => m.DeliveryOptionsModule), data: { preload: 'customer' } },
              { path: '**', redirectTo: 'submissions' }
            ]
          },
        ]
      },
    ]
  },
  { path: 'registration', component: NcRegistrationPage, canActivate: [LogoutGuard] },
  { path: 'signup/v1', component: NewCustomerPageV1, canActivate: [LoginGuard, NotSetupGuard],
    children: [
      { path: '', component: NewCustomerPageV1, canActivate: [EarliestUnfinishedRouteGuardV1] },
      { path: ':customerId', component: NewCustomerPageV1, canActivate: [EarliestUnfinishedRouteGuardV1] },

      { path: ':customerId/about',
        children: [
          { path: '', redirectTo: 'industry', pathMatch: 'full' },
          { path: 'industry', component: IndustryPageV1, canDeactivate: [CanDeactivateIndustryPageV1] },
          { path: 'tone', component: TonePageV1, canDeactivate: [CanDeactivateTonePageV1] },
          { path: 'online_presence', component: OnlinePresencePageV1, canDeactivate: [CanDeactivateOnlinePresencePageV1] },
          { path: 'audience', component: AudienceAndVoicePageV1, canDeactivate: [CanDeactivateAudienceAndVoicePageV1] },
          { path: 'other_info', component: OtherInfoPageV1, canDeactivate: [CanDeactivateOtherInfoPageV1] }
        ]
      },
      { path: ':customerId/topics', component: NcTopicsPageV1 },
      { path: ':customerId/plan', component: NcPlanPageV1 },
      { path: ':customerId/multipass-plan', component: NcMultipassPlanPageV1 },
      { path: ':customerId/payment', component: NcPaymentPageV1 },
    ]
  },
  { path: 'signup', component: NewCustomerPage, canActivate: [LoginGuard, NotSetupGuard],
    children: [
      { path: '', component: NewCustomerPage, canActivate: [EarliestUnfinishedRouteGuard] },
      { path: ':customerId', component: NewCustomerPage, canActivate: [EarliestUnfinishedRouteGuard] },

      { path: ':customerId/about',
        children: [
          { path: '', redirectTo: 'industry', pathMatch: 'full' },
          { path: 'industry', component: IndustryPage, canDeactivate: [CanDeactivateIndustryPage] },
          { path: 'tone', component: TonePage, canDeactivate: [CanDeactivateTonePage] },
          { path: 'online_presence', component: OnlinePresencePage, canDeactivate: [CanDeactivateOnlinePresencePage] },
          { path: 'audience', component: AudienceAndVoicePage, canDeactivate: [CanDeactivateAudienceAndVoicePage] },
          { path: 'other_info', component: OtherInfoPage, canDeactivate: [CanDeactivateOtherInfoPage] }
        ]
      },
      { path: ':customerId/topics', component: NcTopicsPage },
      { path: ':customerId/plan', component: NcPlanPage },
      { path: ':customerId/multipass-plan', component: NcMultipassPlanPage },
      { path: ':customerId/payment', component: NcPaymentPage },
    ]
  },
  { path: 'account/signup', component: NewAccountPage, canActivate: [LoginGuard, NotSetupGuard],
    children: [
      { path: '', component: AccountSignupHomePage },
      { path: 'clients/new', component: ClientNamePage },
      { path: 'clients/home', component: AccountSignupClientsHomePage },
      { path: 'activation', component: NcPaymentPage },
      { path: 'clients/setup', component: ClientConfigurationPage,
        children: [
          { path: ':customerId/business_name', component: ClientNamePage },

          { path: ':customerId/about/industry', component: IndustryPage, canDeactivate: [CanDeactivateIndustryPage] },
          { path: ':customerId/about/tone', component: TonePage, canDeactivate: [CanDeactivateTonePage] },
          { path: ':customerId/about/online_presence', component: OnlinePresencePage, canDeactivate: [CanDeactivateOnlinePresencePage] },
          { path: ':customerId/about/audience', component: AudienceAndVoicePage, canDeactivate: [CanDeactivateAudienceAndVoicePage] },
          { path: ':customerId/about/other_info', component: OtherInfoPage, canDeactivate: [CanDeactivateOtherInfoPage] },

          { path: ':customerId/topics', component: NcTopicsPage },
          { path: ':customerId/plan', component: NcPlanPage },
          { path: ':customerId/multipass-plan', component: NcMultipassPlanPage },
          { path: ':customerId/payment', component: NcPaymentPage },
        ]
      },
    ]
  },
  { path: 'video', loadChildren: () => import('../video-signup/video-signup.module').then(m => m.VideoSignupModule) },
  { path: 'writer_dashboard', canActivate: [ApolloLoginGuard, UserWriterGuard], children: [
    { path: '', loadChildren: () => import('../writer-dashboard/writer-dashboard.module').then(m => m.WriterDashboardModule) },
    { path: 'search', loadChildren: () => import('../search/search.module').then(m => m.SearchModule) },
    { path: 'topic_proposing', loadChildren: () => import('../topic-proposing/topic-proposing.module').then(m => m.TopicProposingModule) }
  ] },
  { path: 'not_found', loadChildren: () => import('../not-found/not-found.module').then(m => m.NotFoundModule) },
  { path: '**', redirectTo: 'not_found' }
]

export const providers = [
  LoginGuard,
  LogoutGuard,
  FullySetupGuard,
  NotSetupGuard,
  EarliestUnfinishedRouteGuard,
  CanDeactivateOnlinePresencePage,
  CanDeactivateAudienceAndVoicePage,
  CanDeactivateIndustryPage,
  CanDeactivateTonePage,
  CanDeactivateOtherInfoPage,
  CanDeactivatePostsPage,
  EarliestUnfinishedRouteGuardV1,
  CanDeactivateOnlinePresencePageV1,
  CanDeactivateAudienceAndVoicePageV1,
  CanDeactivateIndustryPageV1,
  CanDeactivateTonePageV1,
  CanDeactivateOtherInfoPageV1
]

@NgModule({
  imports: [ RouterModule.forRoot(routes, { preloadingStrategy: AppPreloadingStrategy } ) ],
  exports: [ RouterModule ],
  providers: providers
})
export class AppRoutingModule {}
